import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API from "../../../utils/apiCalling";
import { config } from "../../../utils/apiUrl";

const api = new API();

const DEFAULT_GST_RECONCILIATION = {
    summary: {
        totalChecks: 0,
        matched: 0,
        mismatched: 0,
        errors: 0,
        warnings: 0,
        filingReady: false,
    },

    checks: [],

    sections: {
        gstr1VsGstr3b: [],
        gstr1VsHsn: [],
        dataQuality: [],
    },

    values: {
        gstr1: {
            taxableValue: 0,
            igst: 0,
            cgst: 0,
            sgst: 0,
            cess: 0,
            totalTax: 0,
        },

        gstr3b: {
            taxableValue: 0,
            igst: 0,
            cgst: 0,
            sgst: 0,
            cess: 0,
            totalTax: 0,
        },

        hsn: {
            taxableValue: 0,
            totalTax: 0,
        },
    },

    meta: {
        fromDate: null,
        toDate: null,
        financialYear: null,
        tolerance: 1,
        generatedAt: null,
    },
};
/*
|--------------------------------------------------------------------------
| DEFAULT GSTR-3B
|--------------------------------------------------------------------------
*/

/*
|--------------------------------------------------------------------------
| DEFAULT HSN TOTALS
|--------------------------------------------------------------------------
*/

const DEFAULT_HSN_TOTALS = {
    quantity: 0,

    totalValue: 0,

    taxableValue: 0,

    igstAmount: 0,

    cgstAmount: 0,

    sgstAmount: 0,

    cessAmount: 0,

    totalTax: 0,
};


/*
|--------------------------------------------------------------------------
| DEFAULT HSN SUMMARY
|--------------------------------------------------------------------------
*/

const DEFAULT_HSN_SUMMARY = {
    outward: [],

    inward: [],

    totals: {
        outward: {
            ...DEFAULT_HSN_TOTALS,
        },

        inward: {
            ...DEFAULT_HSN_TOTALS,
        },
    },

    counts: {
        outwardHsn: 0,

        inwardHsn: 0,

        salesInvoices: 0,

        purchaseBills: 0,

        creditNotes: 0,

        debitNotes: 0,
    },

    meta: {
        fromDate: null,

        toDate: null,

        financialYear: null,

        generatedAt: null,
    },

    warnings: [],
};


const DEFAULT_GSTR3B_TAX_BUCKET = {
    taxableValue: 0,

    igst: 0,

    cgst: 0,

    sgst: 0,

    cess: 0,

    totalTax: 0,
};

const DEFAULT_GSTR3B = {
    section31: {
        outwardTaxable: {
            ...DEFAULT_GSTR3B_TAX_BUCKET,
        },

        zeroRated: {
            ...DEFAULT_GSTR3B_TAX_BUCKET,
        },

        nilExempt: {
            ...DEFAULT_GSTR3B_TAX_BUCKET,
        },

        nonGst: {
            ...DEFAULT_GSTR3B_TAX_BUCKET,
        },

        reverseCharge: {
            ...DEFAULT_GSTR3B_TAX_BUCKET,
        },
    },

    section4: {
        eligibleItc: {
            ...DEFAULT_GSTR3B_TAX_BUCKET,
        },

        normalItc: {
            ...DEFAULT_GSTR3B_TAX_BUCKET,
        },

        reverseChargeItc: {
            ...DEFAULT_GSTR3B_TAX_BUCKET,
        },

        reversedItc: {
            ...DEFAULT_GSTR3B_TAX_BUCKET,
        },

        ineligibleItc: {
            ...DEFAULT_GSTR3B_TAX_BUCKET,
        },
    },

    taxPosition: {
        output: {
            igst: 0,
            cgst: 0,
            sgst: 0,
            cess: 0,
            total: 0,
        },

        reverseCharge: {
            igst: 0,
            cgst: 0,
            sgst: 0,
            cess: 0,
            total: 0,
        },

        eligibleItc: {
            igst: 0,
            cgst: 0,
            sgst: 0,
            cess: 0,
            total: 0,
        },

        grossLiability: 0,

        availableItc: 0,

        estimatedNetTax: 0,

        excessItc: 0,
    },

    summary: {
        documents: {
            salesInvoices: 0,

            purchaseBills: 0,

            creditNotes: 0,

            debitNotes: 0,
        },

        outwardTaxable: {
            taxableValue: 0,

            tax: 0,
        },

        reverseCharge: {
            taxableValue: 0,

            tax: 0,
        },

        itc: {
            normal: 0,

            reverseCharge: 0,

            total: 0,
        },

        taxPosition: {
            outputTax: 0,

            rcmLiability: 0,

            grossLiability: 0,

            availableItc: 0,

            estimatedNetTax: 0,

            excessItc: 0,
        },
    },

    meta: {
        fromDate: null,

        toDate: null,

        financialYear: null,

        generatedAt: null,
    },

    warnings: [],
};
/*
|--------------------------------------------------------------------------
| DEFAULT GSTR-1 SUMMARY
|--------------------------------------------------------------------------
*/

const DEFAULT_GSTR1_SUMMARY = {
    sales: {
        totalInvoices:
            0,

        b2bInvoices:
            0,

        b2cInvoices:
            0,

        taxableAmount:
            0,

        b2bTaxableAmount:
            0,

        b2cTaxableAmount:
            0,

        cgstAmount:
            0,

        sgstAmount:
            0,

        igstAmount:
            0,

        cessAmount:
            0,

        outputTax:
            0,

        invoiceValue:
            0,
    },

    creditNotes: {
        totalCreditNotes:
            0,

        taxableAmount:
            0,

        cgstAmount:
            0,

        sgstAmount:
            0,

        igstAmount:
            0,

        cessAmount:
            0,

        noteValue:
            0,
    },

    adjusted: {
        taxableAmount:
            0,

        cgstAmount:
            0,

        sgstAmount:
            0,

        igstAmount:
            0,

        cessAmount:
            0,

        outputTax:
            0,

        invoiceValue:
            0,
    },
};


const DEFAULT_GSTR1 = {
    b2b: [],

    b2c: [],

    creditNotes: [],

    summary: {
        ...DEFAULT_GSTR1_SUMMARY,
    },

    meta: {
        fromDate:
            null,

        toDate:
            null,

        financialYear:
            null,

        invoiceCount:
            0,

        creditNoteCount:
            0,
    },

    warnings: [],
};
/*
|--------------------------------------------------------------------------
| DEFAULT PAGINATION
|--------------------------------------------------------------------------
*/

const DEFAULT_PAGINATION = {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
};


/*
|--------------------------------------------------------------------------
| DEFAULT FILTERS
|--------------------------------------------------------------------------
*/

const DEFAULT_FILTERS = {
    fromDate: null,

    toDate: null,

    financialYear: null,

    status: "CONFIRMED",

    taxType: "ALL",

    gstin: "",

    search: "",
};


/*
|--------------------------------------------------------------------------
| PURCHASE GST SUMMARY
|--------------------------------------------------------------------------
*/

const DEFAULT_PURCHASE_SUMMARY = {
    totalDocuments: 0,

    taxableAmount: 0,

    cgstAmount: 0,

    sgstAmount: 0,

    igstAmount: 0,

    cessAmount: 0,

    inputTax: 0,

    grandTotal: 0,

    reverseChargeTaxableAmount: 0,

    reverseChargeTaxAmount: 0,
};


/*
|--------------------------------------------------------------------------
| SALES GST SUMMARY
|--------------------------------------------------------------------------
*/

const DEFAULT_SALES_SUMMARY = {
    totalDocuments: 0,

    taxableAmount: 0,

    cgstAmount: 0,

    sgstAmount: 0,

    igstAmount: 0,

    cessAmount: 0,

    outputTax: 0,

    grandTotal: 0,
};


/*
|--------------------------------------------------------------------------
| GST DASHBOARD SUMMARY
|--------------------------------------------------------------------------
*/

const DEFAULT_DASHBOARD = {
    purchase: {
        ...DEFAULT_PURCHASE_SUMMARY,
    },

    sales: {
        ...DEFAULT_SALES_SUMMARY,
    },

    taxPosition: {
        outputTax: 0,

        inputTax: 0,

        netTaxPayable: 0,

        excessItc: 0,
    },
};


/*
|--------------------------------------------------------------------------
| ERROR HELPER
|--------------------------------------------------------------------------
*/

const getErrorMessage = (
    error,
    fallback = "Something went wrong."
) => {
    if (
        typeof error === "string"
    ) {
        return error;
    }

    return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.data?.message ||
        error?.message ||
        fallback
    );
};


/*
|--------------------------------------------------------------------------
| RESPONSE HELPERS
|--------------------------------------------------------------------------
*/

const getResponseEnvelope = (
    response
) => {
    if (!response) {
        return {};
    }

    if (
        response?.data &&
        typeof response.data === "object" &&
        !Array.isArray(
            response.data
        ) &&
        (
            response?.status !== undefined ||
            response?.headers !== undefined ||
            response?.config !== undefined
        )
    ) {
        return response.data;
    }

    return response;
};


const getResponseData = (
    response
) => {
    const envelope =
        getResponseEnvelope(
            response
        );

    return (
        envelope?.data ??
        envelope
    );
};


/*
|--------------------------------------------------------------------------
| QUERY STRING
|--------------------------------------------------------------------------
*/

const buildQueryString = (
    params = {}
) => {
    const query =
        new URLSearchParams();

    Object.entries(
        params
    ).forEach(
        ([
            key,
            value,
        ]) => {
            if (
                value === undefined ||
                value === null ||
                value === ""
            ) {
                return;
            }

            if (
                [
                    "status",
                    "taxType",
                ].includes(
                    key
                ) &&
                String(
                    value
                ).toUpperCase() ===
                "ALL"
            ) {
                return;
            }

            query.append(
                key,
                value
            );
        }
    );

    const queryString =
        query.toString();

    return queryString
        ? `?${queryString}`
        : "";
};


/*
|--------------------------------------------------------------------------
| NORMALIZE REGISTER RESPONSE
|--------------------------------------------------------------------------
*/

const normalizeRegisterResponse = (
    response,
    defaultSummary
) => {
    const data =
        getResponseData(
            response
        ) || {};

    return {
        records:
            Array.isArray(
                data?.records
            )
                ? data.records
                : [],

        summary: {
            ...defaultSummary,

            ...(data?.summary ||
                {}),
        },

        pagination: {
            ...DEFAULT_PAGINATION,

            ...(data?.pagination ||
                {}),
        },

        filters: {
            ...DEFAULT_FILTERS,

            ...(data?.filters ||
                {}),
        },

        warning:
            data?.warning ||
            null,
    };
};
export const fetchGstReconciliation =
    createAsyncThunk(
        "gstReport/fetchGstReconciliation",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,

                        financialYear:
                            params.financialYear,

                        tolerance:
                            params.tolerance ??
                            1,
                    });


                const response =
                    await api.get(
                        `${config.getGstReconciliation()}${queryString}`
                    );


                return (
                    getResponseData(
                        response
                    ) || {}
                );
            } catch (error) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to reconcile GST data."
                    )
                );
            }
        }
    );
/*
|--------------------------------------------------------------------------
| FETCH HSN SUMMARY
|--------------------------------------------------------------------------
*/

export const fetchHsnSummary =
    createAsyncThunk(
        "gstReport/fetchHsnSummary",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,

                        financialYear:
                            params.financialYear,
                    });


                const response =
                    await api.get(
                        `${config.getHsnSummaryReport()}${queryString}`
                    );


                const data =
                    getResponseData(
                        response
                    ) || {};


                return data;
            } catch (error) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load HSN Summary."
                    )
                );
            }
        }
    );
/*
|--------------------------------------------------------------------------
| FETCH GSTR-3B
|--------------------------------------------------------------------------
*/

export const fetchGstr3bReport =
    createAsyncThunk(
        "gstReport/fetchGstr3bReport",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,

                        financialYear:
                            params.financialYear,
                    });

                const response =
                    await api.get(
                        `${config.getGstr3bReport()}${queryString}`
                    );

                const data =
                    getResponseData(
                        response
                    ) || {};

                return data;
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load GSTR-3B report."
                    )
                );
            }
        }
    );
/*
|--------------------------------------------------------------------------
| FETCH GSTR-1
|--------------------------------------------------------------------------
*/

export const fetchGstr1Report =
    createAsyncThunk(
        "gstReport/fetchGstr1Report",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,

                        financialYear:
                            params.financialYear,
                    });

                const response =
                    await api.get(
                        `${config.getGstr1Report()}${queryString}`
                    );

                const data =
                    getResponseData(
                        response
                    ) || {};

                return data;
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load GSTR-1 report."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH GST DASHBOARD
|--------------------------------------------------------------------------
*/

export const fetchGstDashboard =
    createAsyncThunk(
        "gstReport/fetchGstDashboard",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,

                        financialYear:
                            params.financialYear,
                    });

                const response =
                    await api.get(
                        `${config.getGstDashboard()}${queryString}`
                    );

                const data =
                    getResponseData(
                        response
                    ) || {};

                return {
                    purchase: {
                        ...DEFAULT_PURCHASE_SUMMARY,

                        ...(data?.purchase ||
                            {}),
                    },

                    sales: {
                        ...DEFAULT_SALES_SUMMARY,

                        ...(data?.sales ||
                            {}),
                    },

                    taxPosition: {
                        ...DEFAULT_DASHBOARD
                            .taxPosition,

                        ...(data?.taxPosition ||
                            {}),
                    },
                };
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load GST dashboard."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| FETCH PURCHASE GST REGISTER
|--------------------------------------------------------------------------
*/

export const fetchPurchaseGstRegister =
    createAsyncThunk(
        "gstReport/fetchPurchaseGstRegister",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        page:
                            params.page,

                        limit:
                            params.limit,

                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,

                        financialYear:
                            params.financialYear,

                        status:
                            params.status,

                        taxType:
                            params.taxType,

                        gstin:
                            params.gstin,

                        search:
                            params.search,
                    });

                const response =
                    await api.get(
                        `${config.getPurchaseGstRegister()}${queryString}`
                    );

                return normalizeRegisterResponse(
                    response,
                    DEFAULT_PURCHASE_SUMMARY
                );
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load purchase GST register."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| FETCH SALES GST REGISTER
|--------------------------------------------------------------------------
*/

export const fetchSalesGstRegister =
    createAsyncThunk(
        "gstReport/fetchSalesGstRegister",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        page:
                            params.page,

                        limit:
                            params.limit,

                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,

                        financialYear:
                            params.financialYear,

                        status:
                            params.status,

                        taxType:
                            params.taxType,

                        gstin:
                            params.gstin,

                        search:
                            params.search,
                    });

                const response =
                    await api.get(
                        `${config.getSalesGstRegister()}${queryString}`
                    );

                return normalizeRegisterResponse(
                    response,
                    DEFAULT_SALES_SUMMARY
                );
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load sales GST register."
                    )
                );
            }
        }
    );


/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {
    dashboard: {
        ...DEFAULT_DASHBOARD,
    },


    purchaseRegister: {
        records: [],

        summary: {
            ...DEFAULT_PURCHASE_SUMMARY,
        },

        pagination: {
            ...DEFAULT_PAGINATION,
        },

        filters: {
            ...DEFAULT_FILTERS,
        },

        warning: null,
    },


    salesRegister: {
        records: [],

        summary: {
            ...DEFAULT_SALES_SUMMARY,
        },

        pagination: {
            ...DEFAULT_PAGINATION,
        },

        filters: {
            ...DEFAULT_FILTERS,
        },

        warning: null,
    },


    loading: {
        dashboard: false,

        purchaseRegister: false,

        salesRegister: false,

        gstr1: false,
        gstr3b: false,
        hsnSummary: false,
        reconciliation: false,
    },


    error: {
        dashboard: null,

        purchaseRegister: null,

        salesRegister: null,
        gstr1: null,
        gstr3b: null,
        hsnSummary: null,
        reconciliation: null,
    },
    reconciliation: {
        ...DEFAULT_GST_RECONCILIATION,
    },
    gstr1: {
        ...DEFAULT_GSTR1,

        summary: {
            sales: {
                ...DEFAULT_GSTR1_SUMMARY.sales,
            },

            creditNotes: {
                ...DEFAULT_GSTR1_SUMMARY.creditNotes,
            },

            adjusted: {
                ...DEFAULT_GSTR1_SUMMARY.adjusted,
            },
        },

        meta: {
            ...DEFAULT_GSTR1.meta,
        },

        warnings: [],
    },
    hsnSummary: {
        outward: [],

        inward: [],

        totals: {
            outward: {
                ...DEFAULT_HSN_TOTALS,
            },

            inward: {
                ...DEFAULT_HSN_TOTALS,
            },
        },

        counts: {
            ...DEFAULT_HSN_SUMMARY.counts,
        },

        meta: {
            ...DEFAULT_HSN_SUMMARY.meta,
        },

        warnings: [],
    },
    gstr3b: {
        ...DEFAULT_GSTR3B,

        section31: {
            outwardTaxable: {
                ...DEFAULT_GSTR3B_TAX_BUCKET,
            },

            zeroRated: {
                ...DEFAULT_GSTR3B_TAX_BUCKET,
            },

            nilExempt: {
                ...DEFAULT_GSTR3B_TAX_BUCKET,
            },

            nonGst: {
                ...DEFAULT_GSTR3B_TAX_BUCKET,
            },

            reverseCharge: {
                ...DEFAULT_GSTR3B_TAX_BUCKET,
            },
        },

        section4: {
            eligibleItc: {
                ...DEFAULT_GSTR3B_TAX_BUCKET,
            },

            normalItc: {
                ...DEFAULT_GSTR3B_TAX_BUCKET,
            },

            reverseChargeItc: {
                ...DEFAULT_GSTR3B_TAX_BUCKET,
            },

            reversedItc: {
                ...DEFAULT_GSTR3B_TAX_BUCKET,
            },

            ineligibleItc: {
                ...DEFAULT_GSTR3B_TAX_BUCKET,
            },
        },

        warnings: [],
    },
};


/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const gstReportSlice =
    createSlice({
        name:
            "gstReport",

        initialState,

        reducers: {
            clearGstReconciliation: (
                state
            ) => {
                state.reconciliation = {
                    ...DEFAULT_GST_RECONCILIATION,

                    checks: [],

                    sections: {
                        gstr1VsGstr3b: [],
                        gstr1VsHsn: [],
                        dataQuality: [],
                    },
                };

                state.error.reconciliation =
                    null;
            },
            /*
|--------------------------------------------------------------------------
| CLEAR HSN SUMMARY
|--------------------------------------------------------------------------
*/

            clearHsnSummary: (
                state
            ) => {
                state.hsnSummary = {
                    outward: [],

                    inward: [],

                    totals: {
                        outward: {
                            ...DEFAULT_HSN_TOTALS,
                        },

                        inward: {
                            ...DEFAULT_HSN_TOTALS,
                        },
                    },

                    counts: {
                        ...DEFAULT_HSN_SUMMARY.counts,
                    },

                    meta: {
                        ...DEFAULT_HSN_SUMMARY.meta,
                    },

                    warnings: [],
                };


                state.error.hsnSummary =
                    null;
            },
            /*
            |--------------------------------------------------------------------------
            | CLEAR GSTR-3B
            |--------------------------------------------------------------------------
            */

            clearGstr3bReport: (
                state
            ) => {
                state.gstr3b = {
                    ...DEFAULT_GSTR3B,

                    section31: {
                        outwardTaxable: {
                            ...DEFAULT_GSTR3B_TAX_BUCKET,
                        },

                        zeroRated: {
                            ...DEFAULT_GSTR3B_TAX_BUCKET,
                        },

                        nilExempt: {
                            ...DEFAULT_GSTR3B_TAX_BUCKET,
                        },

                        nonGst: {
                            ...DEFAULT_GSTR3B_TAX_BUCKET,
                        },

                        reverseCharge: {
                            ...DEFAULT_GSTR3B_TAX_BUCKET,
                        },
                    },

                    section4: {
                        eligibleItc: {
                            ...DEFAULT_GSTR3B_TAX_BUCKET,
                        },

                        normalItc: {
                            ...DEFAULT_GSTR3B_TAX_BUCKET,
                        },

                        reverseChargeItc: {
                            ...DEFAULT_GSTR3B_TAX_BUCKET,
                        },

                        reversedItc: {
                            ...DEFAULT_GSTR3B_TAX_BUCKET,
                        },

                        ineligibleItc: {
                            ...DEFAULT_GSTR3B_TAX_BUCKET,
                        },
                    },

                    warnings: [],
                };

                state.error.gstr3b =
                    null;
            },
            /*
|--------------------------------------------------------------------------
| CLEAR GSTR-1
|--------------------------------------------------------------------------
*/

            clearGstr1Report: (
                state
            ) => {
                state.gstr1 = {
                    b2b: [],

                    b2c: [],

                    creditNotes: [],

                    summary: {
                        sales: {
                            ...DEFAULT_GSTR1_SUMMARY
                                .sales,
                        },

                        creditNotes: {
                            ...DEFAULT_GSTR1_SUMMARY
                                .creditNotes,
                        },

                        adjusted: {
                            ...DEFAULT_GSTR1_SUMMARY
                                .adjusted,
                        },
                    },

                    meta: {
                        ...DEFAULT_GSTR1.meta,
                    },

                    warnings: [],
                };

                state.error.gstr1 =
                    null;
            },
            /*
            |--------------------------------------------------------------------------
            | CLEAR PURCHASE REGISTER
            |--------------------------------------------------------------------------
            */

            clearPurchaseGstRegister: (
                state
            ) => {
                state.purchaseRegister = {
                    records: [],

                    summary: {
                        ...DEFAULT_PURCHASE_SUMMARY,
                    },

                    pagination: {
                        ...DEFAULT_PAGINATION,
                    },

                    filters: {
                        ...DEFAULT_FILTERS,
                    },

                    warning: null,
                };

                state.error.purchaseRegister =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR SALES REGISTER
            |--------------------------------------------------------------------------
            */

            clearSalesGstRegister: (
                state
            ) => {
                state.salesRegister = {
                    records: [],

                    summary: {
                        ...DEFAULT_SALES_SUMMARY,
                    },

                    pagination: {
                        ...DEFAULT_PAGINATION,
                    },

                    filters: {
                        ...DEFAULT_FILTERS,
                    },

                    warning: null,
                };

                state.error.salesRegister =
                    null;
            },


            /*
            |--------------------------------------------------------------------------
            | SET PURCHASE FILTERS
            |--------------------------------------------------------------------------
            */

            setPurchaseGstFilters: (
                state,
                action
            ) => {
                state.purchaseRegister.filters = {
                    ...state
                        .purchaseRegister
                        .filters,

                    ...(action.payload ||
                        {}),
                };
            },


            /*
            |--------------------------------------------------------------------------
            | SET SALES FILTERS
            |--------------------------------------------------------------------------
            */

            setSalesGstFilters: (
                state,
                action
            ) => {
                state.salesRegister.filters = {
                    ...state
                        .salesRegister
                        .filters,

                    ...(action.payload ||
                        {}),
                };
            },


            /*
            |--------------------------------------------------------------------------
            | RESET PURCHASE FILTERS
            |--------------------------------------------------------------------------
            */

            resetPurchaseGstFilters: (
                state
            ) => {
                state.purchaseRegister.filters = {
                    ...DEFAULT_FILTERS,
                };
            },


            /*
            |--------------------------------------------------------------------------
            | RESET SALES FILTERS
            |--------------------------------------------------------------------------
            */

            resetSalesGstFilters: (
                state
            ) => {
                state.salesRegister.filters = {
                    ...DEFAULT_FILTERS,
                };
            },


            /*
            |--------------------------------------------------------------------------
            | CLEAR ERRORS
            |--------------------------------------------------------------------------
            */

            clearGstReportErrors: (
                state
            ) => {
                state.error = {
                    dashboard:
                        null,

                    purchaseRegister:
                        null,

                    salesRegister:
                        null,

                    gstr1:
                        null,
                    gstr3b: null,
                    hsnSummary: null,
                };
            },


            /*
            |--------------------------------------------------------------------------
            | RESET FULL STATE
            |--------------------------------------------------------------------------
            */

            resetGstReportState:
                () => ({
                    ...initialState,

                    dashboard: {
                        purchase: {
                            ...DEFAULT_PURCHASE_SUMMARY,
                        },

                        sales: {
                            ...DEFAULT_SALES_SUMMARY,
                        },

                        taxPosition: {
                            ...DEFAULT_DASHBOARD
                                .taxPosition,
                        },
                    },

                    purchaseRegister: {
                        records: [],

                        summary: {
                            ...DEFAULT_PURCHASE_SUMMARY,
                        },

                        pagination: {
                            ...DEFAULT_PAGINATION,
                        },

                        filters: {
                            ...DEFAULT_FILTERS,
                        },

                        warning: null,
                    },

                    salesRegister: {
                        records: [],

                        summary: {
                            ...DEFAULT_SALES_SUMMARY,
                        },

                        pagination: {
                            ...DEFAULT_PAGINATION,
                        },

                        filters: {
                            ...DEFAULT_FILTERS,
                        },

                        warning: null,
                    },

                    loading: {
                        dashboard: false,

                        purchaseRegister: false,

                        salesRegister: false,
                    },

                    error: {
                        dashboard: null,

                        purchaseRegister: null,

                        salesRegister: null,
                    },
                }),
        },


        /*
        |--------------------------------------------------------------------------
        | EXTRA REDUCERS
        |--------------------------------------------------------------------------
        */

        extraReducers: (

            builder
        ) => {
            builder

                .addCase(
                    fetchGstReconciliation.pending,
                    (
                        state
                    ) => {
                        state.loading.reconciliation =
                            true;

                        state.error.reconciliation =
                            null;
                    }
                )


                .addCase(
                    fetchGstReconciliation.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.reconciliation =
                            false;

                        const payload =
                            action.payload ||
                            {};


                        state.reconciliation = {
                            ...DEFAULT_GST_RECONCILIATION,
                            ...payload,

                            summary: {
                                ...DEFAULT_GST_RECONCILIATION
                                    .summary,

                                ...(payload
                                    ?.summary ||
                                    {}),
                            },

                            checks:
                                Array.isArray(
                                    payload?.checks
                                )
                                    ? payload.checks
                                    : [],

                            sections: {
                                gstr1VsGstr3b:
                                    Array.isArray(
                                        payload
                                            ?.sections
                                            ?.gstr1VsGstr3b
                                    )
                                        ? payload.sections
                                            .gstr1VsGstr3b
                                        : [],

                                gstr1VsHsn:
                                    Array.isArray(
                                        payload
                                            ?.sections
                                            ?.gstr1VsHsn
                                    )
                                        ? payload.sections
                                            .gstr1VsHsn
                                        : [],

                                dataQuality:
                                    Array.isArray(
                                        payload
                                            ?.sections
                                            ?.dataQuality
                                    )
                                        ? payload.sections
                                            .dataQuality
                                        : [],
                            },

                            meta: {
                                ...DEFAULT_GST_RECONCILIATION
                                    .meta,

                                ...(payload
                                    ?.meta ||
                                    {}),
                            },
                        };
                    }
                )


                .addCase(
                    fetchGstReconciliation.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.reconciliation =
                            false;

                        state.error.reconciliation =
                            action.payload ||
                            action.error?.message ||
                            "GST reconciliation failed.";
                    }
                );
            /*
            |--------------------------------------------------------------------------
            | DASHBOARD
            |--------------------------------------------------------------------------
            */
            /*
            |--------------------------------------------------------------------------
            | GSTR-1
            |--------------------------------------------------------------------------
            */
            /*
            |--------------------------------------------------------------------------
            | GSTR-3B
            |--------------------------------------------------------------------------
            */
            /*
            |--------------------------------------------------------------------------
            | HSN SUMMARY
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchHsnSummary.pending,

                    (
                        state
                    ) => {
                        state.loading.hsnSummary =
                            true;

                        state.error.hsnSummary =
                            null;
                    }
                )


                .addCase(
                    fetchHsnSummary.fulfilled,

                    (
                        state,
                        action
                    ) => {
                        state.loading.hsnSummary =
                            false;


                        const payload =
                            action.payload ||
                            {};


                        state.hsnSummary = {

                            /*
                            |--------------------------------------------------------------------------
                            | OUTWARD HSN
                            |--------------------------------------------------------------------------
                            */

                            outward:
                                Array.isArray(
                                    payload?.outward
                                )
                                    ? payload.outward
                                    : [],


                            /*
                            |--------------------------------------------------------------------------
                            | INWARD HSN
                            |--------------------------------------------------------------------------
                            */

                            inward:
                                Array.isArray(
                                    payload?.inward
                                )
                                    ? payload.inward
                                    : [],


                            /*
                            |--------------------------------------------------------------------------
                            | TOTALS
                            |--------------------------------------------------------------------------
                            */

                            totals: {
                                outward: {
                                    ...DEFAULT_HSN_TOTALS,

                                    ...(payload
                                        ?.totals
                                        ?.outward ||
                                        {}),
                                },

                                inward: {
                                    ...DEFAULT_HSN_TOTALS,

                                    ...(payload
                                        ?.totals
                                        ?.inward ||
                                        {}),
                                },
                            },


                            /*
                            |--------------------------------------------------------------------------
                            | COUNTS
                            |--------------------------------------------------------------------------
                            */

                            counts: {
                                ...DEFAULT_HSN_SUMMARY
                                    .counts,

                                ...(payload
                                    ?.counts ||
                                    {}),
                            },


                            /*
                            |--------------------------------------------------------------------------
                            | META
                            |--------------------------------------------------------------------------
                            */

                            meta: {
                                ...DEFAULT_HSN_SUMMARY
                                    .meta,

                                ...(payload
                                    ?.meta ||
                                    {}),
                            },


                            /*
                            |--------------------------------------------------------------------------
                            | WARNINGS
                            |--------------------------------------------------------------------------
                            */

                            warnings:
                                Array.isArray(
                                    payload?.warnings
                                )
                                    ? payload.warnings
                                    : [],
                        };
                    }
                )


                .addCase(
                    fetchHsnSummary.rejected,

                    (
                        state,
                        action
                    ) => {
                        state.loading.hsnSummary =
                            false;

                        state.error.hsnSummary =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load HSN Summary.";
                    }
                )
            builder
                .addCase(
                    fetchGstr3bReport
                        .pending,

                    (
                        state
                    ) => {
                        state.loading.gstr3b =
                            true;

                        state.error.gstr3b =
                            null;
                    }
                )

                .addCase(
                    fetchGstr3bReport
                        .fulfilled,

                    (
                        state,
                        action
                    ) => {
                        state.loading.gstr3b =
                            false;

                        const payload =
                            action.payload ||
                            {};


                        state.gstr3b = {
                            section31: {
                                outwardTaxable: {
                                    ...DEFAULT_GSTR3B_TAX_BUCKET,

                                    ...(payload
                                        ?.section31
                                        ?.outwardTaxable ||
                                        {}),
                                },

                                zeroRated: {
                                    ...DEFAULT_GSTR3B_TAX_BUCKET,

                                    ...(payload
                                        ?.section31
                                        ?.zeroRated ||
                                        {}),
                                },

                                nilExempt: {
                                    ...DEFAULT_GSTR3B_TAX_BUCKET,

                                    ...(payload
                                        ?.section31
                                        ?.nilExempt ||
                                        {}),
                                },

                                nonGst: {
                                    ...DEFAULT_GSTR3B_TAX_BUCKET,

                                    ...(payload
                                        ?.section31
                                        ?.nonGst ||
                                        {}),
                                },

                                reverseCharge: {
                                    ...DEFAULT_GSTR3B_TAX_BUCKET,

                                    ...(payload
                                        ?.section31
                                        ?.reverseCharge ||
                                        {}),
                                },
                            },


                            section4: {
                                eligibleItc: {
                                    ...DEFAULT_GSTR3B_TAX_BUCKET,

                                    ...(payload
                                        ?.section4
                                        ?.eligibleItc ||
                                        {}),
                                },

                                normalItc: {
                                    ...DEFAULT_GSTR3B_TAX_BUCKET,

                                    ...(payload
                                        ?.section4
                                        ?.normalItc ||
                                        {}),
                                },

                                reverseChargeItc: {
                                    ...DEFAULT_GSTR3B_TAX_BUCKET,

                                    ...(payload
                                        ?.section4
                                        ?.reverseChargeItc ||
                                        {}),
                                },

                                reversedItc: {
                                    ...DEFAULT_GSTR3B_TAX_BUCKET,

                                    ...(payload
                                        ?.section4
                                        ?.reversedItc ||
                                        {}),
                                },

                                ineligibleItc: {
                                    ...DEFAULT_GSTR3B_TAX_BUCKET,

                                    ...(payload
                                        ?.section4
                                        ?.ineligibleItc ||
                                        {}),
                                },
                            },


                            taxPosition: {
                                ...DEFAULT_GSTR3B
                                    .taxPosition,

                                ...(payload
                                    ?.taxPosition ||
                                    {}),

                                output: {
                                    ...DEFAULT_GSTR3B
                                        .taxPosition
                                        .output,

                                    ...(payload
                                        ?.taxPosition
                                        ?.output ||
                                        {}),
                                },

                                reverseCharge: {
                                    ...DEFAULT_GSTR3B
                                        .taxPosition
                                        .reverseCharge,

                                    ...(payload
                                        ?.taxPosition
                                        ?.reverseCharge ||
                                        {}),
                                },

                                eligibleItc: {
                                    ...DEFAULT_GSTR3B
                                        .taxPosition
                                        .eligibleItc,

                                    ...(payload
                                        ?.taxPosition
                                        ?.eligibleItc ||
                                        {}),
                                },
                            },


                            summary: {
                                ...DEFAULT_GSTR3B
                                    .summary,

                                ...(payload?.summary ||
                                    {}),

                                documents: {
                                    ...DEFAULT_GSTR3B
                                        .summary
                                        .documents,

                                    ...(payload
                                        ?.summary
                                        ?.documents ||
                                        {}),
                                },

                                outwardTaxable: {
                                    ...DEFAULT_GSTR3B
                                        .summary
                                        .outwardTaxable,

                                    ...(payload
                                        ?.summary
                                        ?.outwardTaxable ||
                                        {}),
                                },

                                reverseCharge: {
                                    ...DEFAULT_GSTR3B
                                        .summary
                                        .reverseCharge,

                                    ...(payload
                                        ?.summary
                                        ?.reverseCharge ||
                                        {}),
                                },

                                itc: {
                                    ...DEFAULT_GSTR3B
                                        .summary
                                        .itc,

                                    ...(payload
                                        ?.summary
                                        ?.itc ||
                                        {}),
                                },

                                taxPosition: {
                                    ...DEFAULT_GSTR3B
                                        .summary
                                        .taxPosition,

                                    ...(payload
                                        ?.summary
                                        ?.taxPosition ||
                                        {}),
                                },
                            },


                            meta: {
                                ...DEFAULT_GSTR3B
                                    .meta,

                                ...(payload?.meta ||
                                    {}),
                            },


                            warnings:
                                Array.isArray(
                                    payload
                                        ?.warnings
                                )
                                    ? payload.warnings
                                    : [],
                        };
                    }
                )

                .addCase(
                    fetchGstr3bReport
                        .rejected,

                    (
                        state,
                        action
                    ) => {
                        state.loading.gstr3b =
                            false;

                        state.error.gstr3b =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load GSTR-3B report.";
                    }
                )
            builder
                .addCase(
                    fetchGstr1Report
                        .pending,

                    (
                        state
                    ) => {
                        state.loading.gstr1 =
                            true;

                        state.error.gstr1 =
                            null;
                    }
                )

                .addCase(
                    fetchGstr1Report
                        .fulfilled,

                    (
                        state,
                        action
                    ) => {
                        state.loading.gstr1 =
                            false;

                        const payload =
                            action.payload ||
                            {};

                        state.gstr1 = {
                            b2b:
                                Array.isArray(
                                    payload?.b2b
                                )
                                    ? payload.b2b
                                    : [],

                            b2c:
                                Array.isArray(
                                    payload?.b2c
                                )
                                    ? payload.b2c
                                    : [],

                            creditNotes:
                                Array.isArray(
                                    payload?.creditNotes
                                )
                                    ? payload.creditNotes
                                    : [],

                            summary: {
                                sales: {
                                    ...DEFAULT_GSTR1_SUMMARY
                                        .sales,

                                    ...(payload
                                        ?.summary
                                        ?.sales ||
                                        {}),
                                },

                                creditNotes: {
                                    ...DEFAULT_GSTR1_SUMMARY
                                        .creditNotes,

                                    ...(payload
                                        ?.summary
                                        ?.creditNotes ||
                                        {}),
                                },

                                adjusted: {
                                    ...DEFAULT_GSTR1_SUMMARY
                                        .adjusted,

                                    ...(payload
                                        ?.summary
                                        ?.adjusted ||
                                        {}),
                                },
                            },

                            meta: {
                                ...DEFAULT_GSTR1
                                    .meta,

                                ...(payload?.meta ||
                                    {}),
                            },

                            warnings:
                                Array.isArray(
                                    payload?.warnings
                                )
                                    ? payload.warnings
                                    : [],
                        };
                    }
                )

                .addCase(
                    fetchGstr1Report
                        .rejected,

                    (
                        state,
                        action
                    ) => {
                        state.loading.gstr1 =
                            false;

                        state.error.gstr1 =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load GSTR-1 report.";
                    }
                )
            builder
                .addCase(
                    fetchGstDashboard
                        .pending,

                    (
                        state
                    ) => {
                        state.loading.dashboard =
                            true;

                        state.error.dashboard =
                            null;
                    }
                )

                .addCase(
                    fetchGstDashboard
                        .fulfilled,

                    (
                        state,
                        action
                    ) => {
                        state.loading.dashboard =
                            false;

                        state.dashboard = {
                            purchase: {
                                ...DEFAULT_PURCHASE_SUMMARY,

                                ...(action
                                    .payload
                                    ?.purchase ||
                                    {}),
                            },

                            sales: {
                                ...DEFAULT_SALES_SUMMARY,

                                ...(action
                                    .payload
                                    ?.sales ||
                                    {}),
                            },

                            taxPosition: {
                                ...DEFAULT_DASHBOARD
                                    .taxPosition,

                                ...(action
                                    .payload
                                    ?.taxPosition ||
                                    {}),
                            },
                        };
                    }
                )

                .addCase(
                    fetchGstDashboard
                        .rejected,

                    (
                        state,
                        action
                    ) => {
                        state.loading.dashboard =
                            false;

                        state.error.dashboard =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load GST dashboard.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | PURCHASE GST REGISTER
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchPurchaseGstRegister
                        .pending,

                    (
                        state
                    ) => {
                        state.loading.purchaseRegister =
                            true;

                        state.error.purchaseRegister =
                            null;
                    }
                )

                .addCase(
                    fetchPurchaseGstRegister
                        .fulfilled,

                    (
                        state,
                        action
                    ) => {
                        state.loading.purchaseRegister =
                            false;

                        const payload =
                            action.payload ||
                            {};

                        state.purchaseRegister.records =
                            Array.isArray(
                                payload.records
                            )
                                ? payload.records
                                : [];

                        state.purchaseRegister.summary = {
                            ...DEFAULT_PURCHASE_SUMMARY,

                            ...(payload.summary ||
                                {}),
                        };

                        state.purchaseRegister.pagination = {
                            ...DEFAULT_PAGINATION,

                            ...(payload.pagination ||
                                {}),
                        };

                        state.purchaseRegister.filters = {
                            ...DEFAULT_FILTERS,

                            ...(payload.filters ||
                                {}),
                        };

                        state.purchaseRegister.warning =
                            payload.warning ||
                            null;
                    }
                )

                .addCase(
                    fetchPurchaseGstRegister
                        .rejected,

                    (
                        state,
                        action
                    ) => {
                        state.loading.purchaseRegister =
                            false;

                        state.error.purchaseRegister =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load purchase GST register.";
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | SALES GST REGISTER
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchSalesGstRegister
                        .pending,

                    (
                        state
                    ) => {
                        state.loading.salesRegister =
                            true;

                        state.error.salesRegister =
                            null;
                    }
                )

                .addCase(
                    fetchSalesGstRegister
                        .fulfilled,

                    (
                        state,
                        action
                    ) => {
                        state.loading.salesRegister =
                            false;

                        const payload =
                            action.payload ||
                            {};

                        state.salesRegister.records =
                            Array.isArray(
                                payload.records
                            )
                                ? payload.records
                                : [];

                        state.salesRegister.summary = {
                            ...DEFAULT_SALES_SUMMARY,

                            ...(payload.summary ||
                                {}),
                        };

                        state.salesRegister.pagination = {
                            ...DEFAULT_PAGINATION,

                            ...(payload.pagination ||
                                {}),
                        };

                        state.salesRegister.filters = {
                            ...DEFAULT_FILTERS,

                            ...(payload.filters ||
                                {}),
                        };

                        state.salesRegister.warning =
                            payload.warning ||
                            null;
                    }
                )

                .addCase(
                    fetchSalesGstRegister
                        .rejected,

                    (
                        state,
                        action
                    ) => {
                        state.loading.salesRegister =
                            false;

                        state.error.salesRegister =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load sales GST register.";
                    }
                );
        },
    });


/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
    clearPurchaseGstRegister,

    clearSalesGstRegister,

    setPurchaseGstFilters,

    resetPurchaseGstFilters,

    setSalesGstFilters,

    resetSalesGstFilters,

    clearGstDashboard,

    clearGstr1Report,

    clearGstr3bReport,

    clearGstReportErrors,

    resetGstReportState,
    clearHsnSummary,
    clearGstReconciliation
} =
    gstReportSlice.actions;


/*
|--------------------------------------------------------------------------
| GSTR-3B DEFAULT TAX BUCKET
|--------------------------------------------------------------------------
*/





/*
|--------------------------------------------------------------------------
| GSTR-1 SELECTORS
|--------------------------------------------------------------------------
*/

export const gstr1Selector = (
    state
) =>
    state?.gstReport
        ?.gstr1 ||
    DEFAULT_GSTR1;


export const gstr1B2bSelector = (
    state
) =>
    state?.gstReport
        ?.gstr1
        ?.b2b ||
    [];


export const gstr1B2cSelector = (
    state
) =>
    state?.gstReport
        ?.gstr1
        ?.b2c ||
    [];


export const gstr1CreditNotesSelector = (
    state
) =>
    state?.gstReport
        ?.gstr1
        ?.creditNotes ||
    [];


export const gstr1SummarySelector = (
    state
) =>
    state?.gstReport
        ?.gstr1
        ?.summary ||
    DEFAULT_GSTR1_SUMMARY;


export const gstr1SalesSummarySelector = (
    state
) =>
    state?.gstReport
        ?.gstr1
        ?.summary
        ?.sales ||
    DEFAULT_GSTR1_SUMMARY.sales;


export const gstr1CreditNoteSummarySelector = (
    state
) =>
    state?.gstReport
        ?.gstr1
        ?.summary
        ?.creditNotes ||
    DEFAULT_GSTR1_SUMMARY.creditNotes;


export const gstr1AdjustedSummarySelector = (
    state
) =>
    state?.gstReport
        ?.gstr1
        ?.summary
        ?.adjusted ||
    DEFAULT_GSTR1_SUMMARY.adjusted;


export const gstr1MetaSelector = (
    state
) =>
    state?.gstReport
        ?.gstr1
        ?.meta ||
    DEFAULT_GSTR1.meta;


export const gstr1WarningsSelector = (
    state
) =>
    state?.gstReport
        ?.gstr1
        ?.warnings ||
    [];
/*
|--------------------------------------------------------------------------
| ROOT SELECTOR
|--------------------------------------------------------------------------
*/

export const gstReportStateSelector = (
    state
) =>
    state?.gstReport ||
    initialState;


/*
|--------------------------------------------------------------------------
| DASHBOARD SELECTORS
|--------------------------------------------------------------------------
*/

export const gstDashboardSelector = (
    state
) =>
    state?.gstReport
        ?.dashboard ||
    DEFAULT_DASHBOARD;


export const gstTaxPositionSelector = (
    state
) =>
    state?.gstReport
        ?.dashboard
        ?.taxPosition ||
    DEFAULT_DASHBOARD.taxPosition;


/*
|--------------------------------------------------------------------------
| PURCHASE REGISTER SELECTORS
|--------------------------------------------------------------------------
*/

export const purchaseGstRegisterSelector = (
    state
) =>
    state?.gstReport
        ?.purchaseRegister ||
    initialState.purchaseRegister;


export const purchaseGstRecordsSelector = (
    state
) =>
    state?.gstReport
        ?.purchaseRegister
        ?.records ||
    [];


export const purchaseGstSummarySelector = (
    state
) =>
    state?.gstReport
        ?.purchaseRegister
        ?.summary ||
    DEFAULT_PURCHASE_SUMMARY;


export const purchaseGstPaginationSelector = (
    state
) =>
    state?.gstReport
        ?.purchaseRegister
        ?.pagination ||
    DEFAULT_PAGINATION;


export const purchaseGstFiltersSelector = (
    state
) =>
    state?.gstReport
        ?.purchaseRegister
        ?.filters ||
    DEFAULT_FILTERS;


/*
|--------------------------------------------------------------------------
| SALES REGISTER SELECTORS
|--------------------------------------------------------------------------
*/

export const salesGstRegisterSelector = (
    state
) =>
    state?.gstReport
        ?.salesRegister ||
    initialState.salesRegister;


export const salesGstRecordsSelector = (
    state
) =>
    state?.gstReport
        ?.salesRegister
        ?.records ||
    [];


export const salesGstSummarySelector = (
    state
) =>
    state?.gstReport
        ?.salesRegister
        ?.summary ||
    DEFAULT_SALES_SUMMARY;


export const salesGstPaginationSelector = (
    state
) =>
    state?.gstReport
        ?.salesRegister
        ?.pagination ||
    DEFAULT_PAGINATION;


export const salesGstFiltersSelector = (
    state
) =>
    state?.gstReport
        ?.salesRegister
        ?.filters ||
    DEFAULT_FILTERS;


/*
|--------------------------------------------------------------------------
| LOADING / ERROR
|--------------------------------------------------------------------------
*/

export const gstReportLoadingSelector = (
    state
) =>
    state?.gstReport
        ?.loading ||
    initialState.loading;


export const gstReportErrorSelector = (
    state
) =>
    state?.gstReport
        ?.error ||
    initialState.error;
/*
|--------------------------------------------------------------------------
| GSTR-3B SELECTORS
|--------------------------------------------------------------------------
*/

export const gstr3bSelector = (
    state
) =>
    state?.gstReport
        ?.gstr3b ||
    DEFAULT_GSTR3B;


export const gstr3bSection31Selector = (
    state
) =>
    state?.gstReport
        ?.gstr3b
        ?.section31 ||
    DEFAULT_GSTR3B.section31;


export const gstr3bSection4Selector = (
    state
) =>
    state?.gstReport
        ?.gstr3b
        ?.section4 ||
    DEFAULT_GSTR3B.section4;


export const gstr3bTaxPositionSelector = (
    state
) =>
    state?.gstReport
        ?.gstr3b
        ?.taxPosition ||
    DEFAULT_GSTR3B.taxPosition;


export const gstr3bSummarySelector = (
    state
) =>
    state?.gstReport
        ?.gstr3b
        ?.summary ||
    DEFAULT_GSTR3B.summary;


export const gstr3bMetaSelector = (
    state
) =>
    state?.gstReport
        ?.gstr3b
        ?.meta ||
    DEFAULT_GSTR3B.meta;


export const gstr3bWarningsSelector = (
    state
) =>
    state?.gstReport
        ?.gstr3b
        ?.warnings ||
    [];

/*
|--------------------------------------------------------------------------
| HSN SUMMARY SELECTORS
|--------------------------------------------------------------------------
*/

export const hsnSummarySelector = (
    state
) =>
    state?.gstReport
        ?.hsnSummary ||
    DEFAULT_HSN_SUMMARY;


export const outwardHsnSummarySelector = (
    state
) =>
    state?.gstReport
        ?.hsnSummary
        ?.outward ||
    [];


export const inwardHsnSummarySelector = (
    state
) =>
    state?.gstReport
        ?.hsnSummary
        ?.inward ||
    [];


export const hsnSummaryTotalsSelector = (
    state
) =>
    state?.gstReport
        ?.hsnSummary
        ?.totals ||
    DEFAULT_HSN_SUMMARY.totals;


export const hsnSummaryCountsSelector = (
    state
) =>
    state?.gstReport
        ?.hsnSummary
        ?.counts ||
    DEFAULT_HSN_SUMMARY.counts;


export const hsnSummaryMetaSelector = (
    state
) =>
    state?.gstReport
        ?.hsnSummary
        ?.meta ||
    DEFAULT_HSN_SUMMARY.meta;


export const hsnSummaryWarningsSelector = (
    state
) =>
    state?.gstReport
        ?.hsnSummary
        ?.warnings ||
    [];

export const gstReconciliationSelector = (
    state
) =>
    state?.gstReport
        ?.reconciliation ||
    DEFAULT_GST_RECONCILIATION;


export const gstReconciliationSummarySelector = (
    state
) =>
    state?.gstReport
        ?.reconciliation
        ?.summary ||
    DEFAULT_GST_RECONCILIATION.summary;


export const gstReconciliationChecksSelector = (
    state
) =>
    state?.gstReport
        ?.reconciliation
        ?.checks ||
    [];


export const gstReconciliationSectionsSelector = (
    state
) =>
    state?.gstReport
        ?.reconciliation
        ?.sections ||
    DEFAULT_GST_RECONCILIATION.sections;
/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default gstReportSlice.reducer;